import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import {
  AlertCircle,
  ArrowRight,
  FileText,
  Calendar,
  Building,
  Hash,
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
} from "lucide-react";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric } from "validator";

// TODO: Update this import with your actual API service file and function name
import { getEwayBillV2Details } from "../../../Services/ApiTestService/apiTestService";

function EwayBillV2() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  // Payload States
  const [billNo, setBillNo] = useState("");
  const [billDate, setBillDate] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [docNo, setDocNo] = useState("");

  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(false);
  const { serviceName } = useService();
  const [profileData, setProfileData] = useState(null);

  const isAlpha = (value) => {
    if (!value) return false;
    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));
    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  const handleEwayBillVerification = async () => {
    if (!profileData) return;

    // Basic Validation
    if (!billNo || !billDate || !gstNumber || !docNo) {
      return setInputError("All fields are required to verify the E-way Bill.");
    }

    if (!isAlpha(gstNumber)) {
      return setInputError("⚠️ Please enter a valid alphanumeric GST Number.");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      // Constructing Payload
      const payload = {
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        bill_no: billNo.trim(),
        bill_date: billDate,
        gst_number: gstNumber.trim().toUpperCase(),
        doc_no: docNo.trim(),
      };

      const res = await getEwayBillV2Details(payload);
      setResponse(res);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        const err = res?.error || res?.data?.errors;

        const errMsg = Object.values(err || {});

        errMsg?.map((item, idx) => showErrorToast(item[0]));

        showErrorToast(res?.data?.message || "Invalid details");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "Server error");
      } else if (!res?.success && !res?.status) {
        showErrorToast(res?.data?.message || "Verification failed");
      } else {
        showErrorToast(res?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // UI Helper for rendering data rows neatly
  const renderRow = (label, value) => {
    if (!value && value !== 0) return null; // Hide if empty
    return (
      <div className="flex justify-between items-start border-b border-slate-100 pb-2 last:border-0 last:pb-0">
        <span className="font-semibold text-slate-500 w-[45%] text-left">
          {label}:
        </span>
        <span className="text-slate-900 font-medium text-right w-[55%] break-words">
          {value}
        </span>
      </div>
    );
  };

  // Extract nested data for cleaner JSX below
  const billData = response?.data?.data;

  return (
    <>
      <Seo />
      <Header heading={serviceName || "E-way Bill V2"} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin text-blue-600" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 px-4">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full mt-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <FileText size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                E-way Bill V2 Verification
              </h2>
              <p className="text-sm text-slate-500">
                Verify E-way bill details using the bill number, date, GSTIN,
                and doc number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Bill Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileText size={17} className="text-indigo-600" />
                Bill Number  <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={billNo}
                onChange={(e) => setBillNo(e.target.value)}
                placeholder="Eg: 68576456"
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Bill Date */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Calendar size={17} className="text-blue-600" />
                Bill Date  <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm transition-all duration-200 text-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* GST Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Building size={17} className="text-green-600" />
                GST Number  <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                placeholder="Eg: 27XXXXXXC1ZP"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Document Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Hash size={17} className="text-orange-500" />
                Document Number (Doc No)  <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={docNo}
                onChange={(e) => setDocNo(e.target.value)}
                placeholder="Eg: 55"
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>
          </div>

          {/* Error Message */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-8 flex justify-end">
            <button
              disabled={
                isLoading || !billNo || !billDate || !gstNumber || !docNo
              }
              onClick={handleEwayBillVerification}
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify E-way Bill
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
        </div>

        {/* ---------------- RESPONSE MESSAGE ---------------- */}
        {response && (
          <div
            className={` ${
              response.status?.code === 500 || response.status?.code === 422
                ? "text-red-500"
                : "text-green-600"
            } py-4 text-xl md:text-2xl font-bold text-center mt-4`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* ---------------- INSTRUCTIONS ---------------- */}
        <div className="w-full flex justify-center">
          <div className="bg-blue-50 max-w-3xl border border-blue-200 text-blue-800 p-5 mt-8 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info size={20} /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>
            <p className="text-sm leading-relaxed">
              Please enter valid details for all fields: Bill Number, Bill Date,
              GST Number, and Document Number. After clicking the Verify button,
              the system will process the payload and return the E-way Bill
              details in a structured format. You can review and copy the JSON
              data for your reference.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT SECTION ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl items-start">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-xl shadow-lg overflow-auto max-h-[800px]">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-4 bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                title="Copy JSON"
              >
                {isJsonCopied ? (
                  <span className="text-sm font-bold text-green-400">
                    Copied
                  </span>
                ) : (
                  <Copy size={18} />
                )}
              </button>
              <p className="font-bold mb-4 text-xl flex items-center justify-start gap-2 text-white border-b border-gray-700 pb-3">
                <FileJson /> JSON Response
              </p>
              <pre className="text-sm font-mono whitespace-pre-wrap">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- STRUCTURED DATA VIEW -------- */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 h-full">
              <h2 className="text-xl flex items-center justify-start gap-2 font-bold text-slate-800 mb-6 border-b border-gray-300 pb-3">
                <ListCheck className="text-blue-600" />
                E-way Bill Details
              </h2>

              {billData && (
                <div className="flex flex-col gap-5">
                  {/* General Info Card */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                    <h3 className="text-xs font-bold text-slate-400 mb-3 uppercase tracking-widest">
                      General Information
                    </h3>
                    <div className="flex flex-col gap-3 text-sm">
                      {renderRow("Unique No", billData.unique_no)}
                      {renderRow("Entered Date", billData.entered_date)}
                      {renderRow("Valid From", billData.valid_from)}
                      {renderRow("Document No", billData.document_no)}
                      {renderRow("Document Date", billData.document_date)}
                      {renderRow("Transaction Type", billData.transaction_type)}
                    </div>
                  </div>

                  {/* Parties Involved Card */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                    <h3 className="text-xs font-bold text-slate-400 mb-3 uppercase tracking-widest">
                      Parties Involved
                    </h3>
                    <div className="flex flex-col gap-3 text-sm">
                      {renderRow("Supplier GSTIN", billData.gstin_of_supplier)}
                      {renderRow(
                        "Place of Dispatch",
                        billData.place_of_dispatch,
                      )}
                      {renderRow(
                        "Recipient GSTIN",
                        billData.gstin_of_recipient,
                      )}
                      {renderRow(
                        "Place of Delivery",
                        billData.place_of_delivery,
                      )}
                    </div>
                  </div>

                  {/* Goods & Transportation Card */}
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                    <h3 className="text-xs font-bold text-slate-400 mb-3 uppercase tracking-widest">
                      Goods & Transport
                    </h3>
                    <div className="flex flex-col gap-3 text-sm">
                      {renderRow("Value of Goods (₹)", billData.value_of_goods)}
                      {renderRow("HSN Code", billData.hsn_code)}
                      {renderRow(
                        "Reason for Transport",
                        billData.reason_for_transportation,
                      )}
                      {renderRow("Transporter", billData.transporter || "N/A")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default EwayBillV2;
